import {AttrsSchema, Component} from 'panel';
import ResizeObserver from 'resize-observer-polyfill';

import {IronContextRegistry} from 'common/report/context-registry';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {UserLocalStorageHelper} from 'common/report/user-local-storage';
import {DummyStorageManager} from 'common/contexts/storage-manager/dummy';
import {LocalStorageKey} from 'common/report/constants';
import {userCookieUtils} from 'common/report/util/user-identity-from-cookie';
import {emitTrackEvent, MOUSE_BUTTONS} from 'common/util/dom';
import {generateShortcutLabel, keyEventSwitcher} from 'common/util/keyboard';
import {getReportName} from 'common/report/util';
import Layout from 'common/stylesheets/mixins/layout.json';

import {Placement} from '../tooltip/types';
import {Direction, PanelAttrs, PanelJsxProps, PanelState, PersistedPanelState} from './types';
import {
  CollapsePanelSource,
  DEFAULT_PERSISTED_STATE,
  ExpandPanelSource,
  HORIZONTAL_DIRECTIONS,
  KEYDOWN_EVENTS_CHARACTER,
  NEGATIVE_DIRECTIONS,
  OtherTogglePanelSource,
  PANEL_COLLAPSED_WIDTH,
  PANEL_DEFAULT_WIDTH,
  PANEL_MAX_AUTO_WIDTH,
  PANEL_MAX_WIDTH,
  PANEL_MIN_WIDTH,
  PANEL_RESIZE_SNAP_WINDOW,
  ResetPanelSource,
  TogglePanelSource,
  TrackingEvent,
  VERTICAL_DIRECTIONS,
} from './constants';
import {template} from './template';
import css from './index.inline.styl';

const COMPONENT_NAME = `mp-panel`;
const {PAGE_MIN_WIDTH} = Layout;

export class Panel extends Component<PanelState, PanelAttrs, any, any, IronContextRegistry> {
  private resizeHandler: (ev: MouseEvent) => void;
  private userLocalStorageHelper: UserLocalStorageHelper<PersistedPanelState>;
  private containerResizeObserver: ResizeObserver;
  private _containerElement: HTMLElement;

  private hasSetPersistedState = false;

  static get attrsSchema(): AttrsSchema<PanelAttrs> {
    return {
      'disable-collapsing': `boolean`,
      loading: {type: `boolean`},
      'min-adjacent-size': {
        type: `number`,
        default: PAGE_MIN_WIDTH - PANEL_MIN_WIDTH,
        description: `minimum size for the adjacent content on the page. used to block resizing and collapsing the panel when necessary.`,
      },
      'min-size': {
        type: `number`,
        default: PANEL_MIN_WIDTH,
        description: `minimum size for the panel content`,
      },
      'max-size': {
        type: `number`,
        default: PANEL_MAX_WIDTH,
        description: `maximum size for the panel content`,
      },
      'max-auto-size': {
        type: `number`,
        default: PANEL_MAX_AUTO_WIDTH,
        description: `suggested size presented to the user by default`,
      },
      'default-size': {
        type: `number`,
        default: PANEL_DEFAULT_WIDTH,
        description: `default size of the panel content`,
      },
      'storage-key': {type: `string`, default: `default`},
      'show-collapse-button': `boolean`,
      'extra-actions': {type: `json`, description: `Extra actions to show above the collapse button`, default: []},
      'panel-name': `string`,
      'resize-direction': {type: `string`, default: Direction.Right},
      'collapse-key': `string`,
      'collapse-key-modifier': {type: `string`, tsType: `KeyModifiers`},
      'default-ratio': `number`,
      'fixed-size': `boolean`,
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      contexts: [`sessionStorageManager` as const],
      defaultContexts: {
        sessionStorageManager: new DummyStorageManager(),
      },
      defaultState: {
        canExpand: true,
        hasResized: false,
        isResizing: false,
        containerWidth: 0,
        containerHeight: 0,
        ...DEFAULT_PERSISTED_STATE,
      },
      hooks: {
        postUpdate: (_state: PanelState) => {
          this.applyStyles();
        },
      },
      helpers: {
        getCollapseButtonIconDirection: () => {
          const {isCollapsed} = this.state;

          switch (this.attr(`resize-direction`)) {
            case Direction.Top:
              return isCollapsed ? `push-up` : `push-down`;
            case Direction.Right:
              return isCollapsed ? `push-right` : `push-left`;
            default:
              return null;
          }
        },

        getSideButtonTooltipDirection: () => {
          const {isCollapsed} = this.state;

          switch (this.attr(`resize-direction`)) {
            case Direction.Top:
              return isCollapsed ? Placement.Top : Placement.RightCenter;
            case Direction.Right:
              return isCollapsed ? Placement.RightEnd : Placement.Top;
            default:
              return null;
          }
        },

        handleExtraActionClick: ({value, icon, label}) => {
          this.track(TrackingEvent.ExtraActionClicked, {...this.state, action: value, icon, label});
          this.dispatchEvent(new CustomEvent(`actionclick`, {detail: {value}}));
        },
      },
    };
  }

  getContainerElement() {
    return this._containerElement || this.parentElement;
  }

  connectedCallback() {
    super.connectedCallback();

    this.userLocalStorageHelper = new UserLocalStorageHelper({
      context: this.getContext(`sessionStorageManager`),
      namespace: LocalStorageKey.Panel,
    });

    const setContainerSizes = (rect: DOMRect) => {
      const {width: containerWidth, height: containerHeight} = rect;
      this.update({containerWidth, containerHeight});
      this.handleContainerResize();
    };

    this.containerResizeObserver = new ResizeObserver((entries) => {
      setContainerSizes(entries[0].contentRect);
    });
    this.containerResizeObserver.observe(this.getContainerElement());

    // Need to call this synchronously so that this element's size is set immediately, since other components might
    // rely on this element having the proper size (e.g. <mp-data-table> needs this to determine number of rows to
    // render)
    setContainerSizes(this.getContainerElement().getBoundingClientRect());

    document.addEventListener(`keydown`, this.operatingSystemKeySwitcher);
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    super.attributeChangedCallback(name, oldValue, newValue);

    if (name === `fixed-size`) {
      if (!this.attr(`disable-collapsing`) && this.attr(`fixed-size`) && this.state.isCollapsed) {
        this.toggleCollapsed({source: ExpandPanelSource.SwitchToFixedSize});
      } else if (!this.attr(`fixed-size`)) {
        this.handleContainerResize();
      }
    }
  }

  isHorizontallyResizable() {
    return HORIZONTAL_DIRECTIONS.has(this.attr(`resize-direction`));
  }

  isVerticallyResizable() {
    return VERTICAL_DIRECTIONS.has(this.attr(`resize-direction`));
  }

  isNegativeDirection() {
    return NEGATIVE_DIRECTIONS.has(this.attr(`resize-direction`));
  }

  /** Changes keys based on operating system we're using and triggers toggleCollapsed */
  operatingSystemKeySwitcher = (ev) => {
    if (!this.attr(`disable-collapsing`) && !this.attr(`fixed-size`) && this.attr(`collapse-key`)) {
      let key = this.attr(`collapse-key`);
      if (this.attr(`collapse-key-modifier`)) {
        key = `${this.attr(`collapse-key-modifier`)}+${key}`;
      }

      return keyEventSwitcher(
        {
          [key]: () => {
            this.toggleCollapsed({source: OtherTogglePanelSource.KeyboardShortcut, keyboardShortcut: key});
          },
        },
        {stopPropagation: true},
      )(ev);
    }
  };

  disconnectedCallback() {
    this.containerResizeObserver.disconnect();

    document.removeEventListener(`keydown`, this.operatingSystemKeySwitcher);

    super.disconnectedCallback();
  }

  get collapseShortcutLabel() {
    if (!this.attr(`collapse-key`)) {
      return null;
    }

    return generateShortcutLabel(KEYDOWN_EVENTS_CHARACTER[this.attr(`collapse-key`)], {
      ...(this.attr(`collapse-key-modifier`) ? {modifiers: [this.attr(`collapse-key-modifier`)]} : {}),
      forMarkdown: true,
    });
  }

  get defaultSize() {
    return this.getOptimalSize(true);
  }

  /**
   * maxSize is determined either by the `max-size` attr (e.g. sidebar) or if that isn't present, then it is determined by the `min-adjacent-size` attr (e.g. data table).
   */
  get maxSize() {
    if (this.attr(`max-size`)) {
      // this path is currently only used for the sidebar panel
      return this.attr(`max-size`);
    } else {
      // this path is currently only used for the data table panel
      return this.totalSpace - this.attr(`min-adjacent-size`);
    }
  }

  /**
   * Total space available for the current panel and the adjacent element.
   */
  get totalSpace() {
    return this.isVerticallyResizable() ? this.state.containerHeight : this.state.containerWidth;
  }

  startResizing(ev: MouseEvent) {
    if (ev.button !== MOUSE_BUTTONS.LEFT) {
      return;
    }

    // handle a double click event
    // https://developer.mozilla.org/en-US/docs/Web/API/UIEvent/detail
    if (ev.detail === 2) {
      return this.resetResizingState({source: ResetPanelSource.DoubleClick});
    }

    ev.preventDefault();

    this.update({isResizing: true});

    const {size} = this.state;
    const initialPosition = this.isHorizontallyResizable() ? ev.clientX : ev.clientY;

    this.resizeHandler = (mouseMoveEvent: MouseEvent) => this.onPanelResize(initialPosition, size, mouseMoveEvent);

    window.addEventListener(`mousemove`, this.resizeHandler);
    window.addEventListener(`mouseup`, this.onPanelStopResize);

    // prevent mouse interactions with other elements in the page
    document.body.style.pointerEvents = `none`;
  }

  /**
   * Resets any changes from the user and
   * defaults to the suggested width
   */
  resetResizingState({source}: {source: ResetPanelSource}) {
    this.userLocalStorageHelper.removeItem({
      userId: userCookieUtils.getUserIdentityFromCookie()?.id,
      key: this.attr(`storage-key`),
    });

    this.track(TrackingEvent.Reset, {...this.state, source, totalSpace: this.totalSpace});

    this.update({
      ...DEFAULT_PERSISTED_STATE,
      size: this.defaultSize,
      currentRatio: this.attr(`default-ratio`),
      hasResized: false,
      snapResetState: false,
    });

    this.dispatchResizeEvent();
  }

  private onPanelStopResize = () => {
    document.body.style.pointerEvents = ``;
    window.removeEventListener(`mousemove`, this.resizeHandler);
    window.removeEventListener(`mouseup`, this.onPanelStopResize);
    if (this.state.snapResetState) {
      // when snap to default happens, reset user resizing stored settings
      this.resetResizingState({source: ResetPanelSource.SnapToDefault});
    }
    this.update({isResizing: false});
    this.setPersistedState();
    this.track(TrackingEvent.Resize, {
      size: this.state.size,
      currentRatio: this.state.currentRatio,
      totalSpace: this.totalSpace,
    });
    this.resizeHandler = null;
  };

  private onPanelResize = (initialPosition: number, initialSize: number, ev: MouseEvent) => {
    ev.stopPropagation();
    ev.preventDefault();

    const currentPosition = this.isHorizontallyResizable() ? ev.clientX : ev.clientY;

    const delta = currentPosition - initialPosition;
    const maxSize = this.maxSize;

    let snapResetState = false;
    const newSize = initialSize + (this.isNegativeDirection() ? -delta : delta);
    let newAdjustedSize: number;
    if (maxSize && !this.state.isCollapsed && newSize > maxSize) {
      // Cap the size if there's no more room
      newAdjustedSize = maxSize;
    } else if (this.defaultSize && Math.abs(newSize - this.defaultSize) < PANEL_RESIZE_SNAP_WINDOW) {
      // "Snap" to the default width if it's within the window
      newAdjustedSize = this.defaultSize;
      snapResetState = true;
    } else if (this.attr(`min-size`) && maxSize) {
      // Apply min/max bounds
      newAdjustedSize = Math.min(Math.max(newSize, this.attr(`min-size`)), maxSize);
    }

    this.update({size: newAdjustedSize});

    // ratio of the panel's size relative to the total space available.
    const currentRatio = newAdjustedSize / this.totalSpace;
    this.update({currentRatio});

    // collapse when being resized closed to the edge of the screen
    if (!this.attr(`disable-collapsing`) && this.attr(`min-size`) && newSize <= this.attr(`min-size`) / 2) {
      this.update({size: initialSize});
      this.onPanelStopResize();
      this.toggleCollapsed({source: CollapsePanelSource.ResizedToEdge});
    } else {
      this.update({hasResized: true, snapResetState});
      this.dispatchResizeEvent();
    }
  };

  private dispatchResizeEvent() {
    const size = this.state.isCollapsed ? PANEL_COLLAPSED_WIDTH : this.state.size;
    this.dispatchEvent(new CustomEvent(`panelResize`, {detail: {size}}));
  }

  getPersistedState(): PersistedPanelState & {hasResized: boolean} {
    const persistedState = this.userLocalStorageHelper.getItem({
      key: this.attr(`storage-key`),
      userId: userCookieUtils.getUserIdentityFromCookie()?.id,
    });

    if (!persistedState) {
      return {
        ...DEFAULT_PERSISTED_STATE,
        size: this.defaultSize,
        currentRatio: this.attr(`default-ratio`),
        hasResized: false,
      };
    }

    const size = persistedState.size
      ? Math.max(this.attr(`min-size`), Math.min(persistedState.size, this.maxSize))
      : this.defaultSize;

    const currentRatio = size / this.totalSpace;

    return {
      isCollapsed: persistedState.isCollapsed,
      size,
      hasResized: Boolean(persistedState.size),
      currentRatio,
    };
  }

  private setPersistedState() {
    const {isCollapsed, size, hasResized, currentRatio} = this.state;
    const persistedState = {
      isCollapsed,
      size: hasResized ? size : null,
      currentRatio,
    };

    this.userLocalStorageHelper.setItem({
      userId: userCookieUtils.getUserIdentityFromCookie()?.id,
      key: this.attr(`storage-key`),
      value: persistedState,
    });
  }

  handleContainerResize() {
    if (!this.attr(`fixed-size`)) {
      if (this.state.containerWidth && this.state.containerHeight && !this.hasSetPersistedState) {
        this.update(this.getPersistedState());
        this.dispatchResizeEvent();
        this.hasSetPersistedState = true;
      } else {
        const newSize = this.getOptimalSize();
        // prevent automatic resizing when the sidebar is in overlay mode
        if (this.state.size !== newSize) {
          this.update({size: newSize});
          this.update({currentRatio: newSize / this.totalSpace});
          this.dispatchResizeEvent();
          this.applyStyles();
        }

        this.applyStyles();
      }
    } else {
      this.update({size: this.attr(`default-size`)});
      this.dispatchResizeEvent();
    }
  }

  toggleCollapsed({source, keyboardShortcut}: {source: TogglePanelSource; keyboardShortcut?: string}) {
    const isCollapsed = !this.state.isCollapsed;
    this.update({isCollapsed});
    this.dispatchResizeEvent();
    this.setPersistedState();
    this.track(isCollapsed ? TrackingEvent.CollapsedPanel : TrackingEvent.ExpandedPanel, {
      source,
      size: this.state.size,
      currentRatio: this.state.currentRatio,
      totalSpace: this.totalSpace,
      keyboardShortcut,
    });
  }

  private track(eventName: TrackingEvent, props = {}) {
    emitTrackEvent(this, {
      eventName,
      eventPrefix: COMPONENT_NAME,
      props: {
        tab: getReportName(),
        canUseResizableLayout: true,
        panelName: this.attr(`panel-name`),
        ...props,
      },
    });
  }

  set containerEl(val) {
    this._containerElement = val;
  }

  /**
   * Defines the panel size as this.attr(`default-ratio`)% of the container size.
   *
   * Sidebar is clamped by the MIN width and 560px as MAX for the
   * default provided widths
   */
  private getOptimalSize(defaultSize?: boolean) {
    if (!this.totalSpace) {
      return this.attr(`default-size`);
    }

    const relativeSize = Math.floor(
      (defaultSize ? this.attr(`default-ratio`) : this.state.currentRatio) * this.totalSpace,
    );
    return Math.max(
      this.attr(`min-size`),
      Math.min(relativeSize, !defaultSize || this.isVerticallyResizable() ? this.maxSize : PANEL_MAX_AUTO_WIDTH),
    );
  }

  private applyStyles() {
    const {isCollapsed, size} = this.state;

    const styleAttr = this.isHorizontallyResizable() ? `width` : `height`;
    this.style[styleAttr] = isCollapsed ? `` : `${size}px`;

    isCollapsed ? this.classList.add(`collapsed`) : this.classList.remove(`collapsed`);
  }
}

export const PanelEl = defineAndMakeJsxFactory<PanelJsxProps>(COMPONENT_NAME, Panel);
export const PanelReact = panel2react<PanelJsxProps>(COMPONENT_NAME, Panel);
